import { fetchApi } from './api.js';
import { printLine, setPromptTo, resetPrompt } from './terminal.js';
import { CONFIG, CSS_CLASSES, REGEX_PATTERNS, API_ENDPOINTS, MESSAGES, WIZARD_STEPS, escapeHtml } from './constants.js';

/**
 * Mutable state object for the multi-step contact form wizard.
 * When {@code active} is true, all terminal input is routed here instead of the
 * normal command processor.
 */
export let contactWizard = {
    active: false,
    step: WIZARD_STEPS.NAME,
    data: { name: CONFIG.EMPTY, email: CONFIG.EMPTY, message: CONFIG.EMPTY },
};

/** Initialises and starts the interactive contact form wizard. */
export function startContactWizard() {
    contactWizard.active = true;
    contactWizard.step = WIZARD_STEPS.NAME;
    contactWizard.data = { name: CONFIG.EMPTY, email: CONFIG.EMPTY, message: CONFIG.EMPTY };

    setPromptTo(MESSAGES.WIZARD_NAME_PROMPT);
    printLine(MESSAGES.WIZARD_START, CSS_CLASSES.TITLE);
}

/**
 * Processes a single line of user input within the active wizard session.
 * Each call advances the step counter or re-prompts on validation failure.
 *
 * @param {string} inputVal - Raw input from the terminal
 */
export function executeContactWizard(inputVal) {
    const trimmed = inputVal.trim();
    if (trimmed === CONFIG.EXIT || trimmed === CONFIG.CANCEL) {
        cancelContactWizard();
        return;
    }

    switch (contactWizard.step) {
        case WIZARD_STEPS.NAME: { // Collect name
            if (!trimmed) {
                printLine(MESSAGES.WIZARD_NAME_EMPTY, CSS_CLASSES.WARNING);
                return;
            }
            contactWizard.data.name = trimmed;
            printLine(MESSAGES.WIZARD_NAME_ECHO(contactWizard.data.name), CSS_CLASSES.COMMENT);
            contactWizard.step = WIZARD_STEPS.EMAIL;
            setPromptTo(MESSAGES.WIZARD_EMAIL_PROMPT);
            break;
        }

        case WIZARD_STEPS.EMAIL: { // Collect email
            if (!REGEX_PATTERNS.EMAIL.test(trimmed)) {
                printLine(MESSAGES.WIZARD_EMAIL_INVALID, CSS_CLASSES.WARNING);
                return;
            }
            contactWizard.data.email = trimmed;
            printLine(MESSAGES.WIZARD_EMAIL_ECHO(contactWizard.data.email), CSS_CLASSES.COMMENT);
            contactWizard.step = WIZARD_STEPS.MESSAGE;
            setPromptTo(MESSAGES.WIZARD_MESSAGE_PROMPT);
            break;
        }

        case WIZARD_STEPS.MESSAGE: { // Collect message, then POST to API
            if (!trimmed) {
                printLine(MESSAGES.WIZARD_MESSAGE_EMPTY, CSS_CLASSES.WARNING);
                return;
            }
            contactWizard.data.message = trimmed;
            printLine(MESSAGES.WIZARD_MESSAGE_ECHO(contactWizard.data.message), CSS_CLASSES.COMMENT);

            const promise = fetchApi(API_ENDPOINTS.CONTACT, (res) => {
                if (res.status === CONFIG.SUCCESS) {
                    printLine(`\n${escapeHtml(res.message)}`, CSS_CLASSES.SUCCESS);
                } else {
                    printLine(`\nFailed to send message: ${escapeHtml(res.message)}`, CSS_CLASSES.ERROR);
                }
                resetPrompt();
                contactWizard.active = false;
            }, true, contactWizard.data);

            if (promise && typeof promise.catch === 'function') {
                promise.catch(() => {
                    resetPrompt();
                    contactWizard.active = false;
                });
            }
            break;
        }
    }
}

/** Aborts the wizard and restores the normal terminal prompt. */
export function cancelContactWizard() {
    printLine(MESSAGES.WIZARD_CANCELLED, CSS_CLASSES.WARNING);
    resetPrompt();
    contactWizard.active = false;
}
